/**
 * InfoBar — thin black bar between viewer and shot lane.
 * Shows scene heading + shot metadata (moved from TopHeader center).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import styles from './InfoBar.module.css';

export function InfoBar() {
  const { t } = useTranslation('timeline');
  const project = useProjectStore(s => s.project);
  const currentTime = usePlaybackStore(s => s.currentTime);
  const getShotAtTime = useProjectStore(s => s.getShotAtTime);
  const getSceneForShot = useProjectStore(s => s.getSceneForShot);

  const shot = getShotAtTime(currentTime);
  const scene = shot ? getSceneForShot(shot.id) : null;

  // filter(Boolean)-joined so an empty location name (no location set) doesn't leave a stray
  // double space; mirrors composeSceneCaptionRest (sceneCaption.ts).
  const heading = shot && scene
    ? [
        [scene.locationType ? `${scene.locationType}.` : '', scene.name.toUpperCase()].filter(Boolean).join(' '),
        scene.timeOfDay,
      ].filter(Boolean).join(' \u2014 ')
    : null;

  const meta = useMemo(() => {
    if (!shot) return project?.title ?? '';
    const parts: string[] = [t('infoBarShotLabel', { number: shot.number }), `${shot.focalLength}mm`, `f/${shot.fStop}`];
    if (shot.shotSize) parts.push(shot.shotSize);
    if (shot.shotAngle) parts.push(shot.shotAngle);
    if (shot.shotMovement) parts.push(shot.shotMovement);
    return parts.join(' \u2022 ');
  }, [shot, project?.title, t]);

  return (
    <div className={styles.bar}>
      {heading && <span className={styles.heading}>{heading}</span>}
      {heading && <span className={styles.dot}>{'\u2022'}</span>}
      <span className={styles.meta}>{meta}</span>
    </div>
  );
}
